import React, { forwardRef, ReactNode } from "react";
import { FontAwesomeIcon, type FontAwesomeIconProps } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import styles from "./IconComponent.module.css";
import { getFilteredDataAttributes } from "../../utils";

type IconSize = "2xs" | "xs" | "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl" | "8xl";

type DataAttributes = {
	[key: `data-${string}`]: string | number | undefined;
};
type SvgIconComponent = React.ComponentType<React.SVGProps<SVGSVGElement>>;

type IconSource = IconProp | SvgIconComponent;
interface IconComponentProps extends Omit<FontAwesomeIconProps, "size" | "children" | "icon">, DataAttributes {
	autoTranslate?: "yes" | "no";
	children?: ReactNode;
	className?: string;
	color?: string;
	fontSize?: string | number;
	size?: IconSize;
	icon?: IconSource;
}
const isFontAwesomeIcon = (icon?: IconSource): icon is IconProp => typeof icon === "object" && "prefix" in icon;

const IconComponent = forwardRef<HTMLElement, IconComponentProps>(
	(
		{
			autoTranslate = "yes",
			children,
			className = "",
			color = "var(--color-black)",
			fontSize,
			icon,
			onMouseEnter,
			onMouseLeave,
			onMouseOver,
			size = "sm",
			spin = false,
			rotation,
			...rest
		},
		ref
	) => {
		if (!icon && !children) {
			return null;
		}
		const filteredDataAttributes = getFilteredDataAttributes(rest) as DataAttributes;
		if (!children && icon && !isFontAwesomeIcon(icon)) {
			const SvgIcon = icon;
			return (
				<SvgIcon
					ref={ref as React.Ref<SVGSVGElement>}
					className={`${styles.icon} ${className}`}
					data-size={size}
					data-spin={spin}
					style={{ color, fontSize, transform: `rotate(${rotation}deg)` }}
					onMouseEnter={onMouseEnter}
					onMouseLeave={onMouseLeave}
					onMouseOver={onMouseOver}
					{...filteredDataAttributes}
				/>
			);
		}
		if (!children && isFontAwesomeIcon(icon)) {
			return (
				<FontAwesomeIcon
					ref={ref as React.Ref<SVGSVGElement>}
					className={`${styles.icon} ${className}`}
					color={color}
					data-size={size}
					fontSize={fontSize}
					icon={icon}
					rotation={rotation}
					spin={spin}
					onMouseEnter={onMouseEnter}
					onMouseLeave={onMouseLeave}
					onMouseOver={onMouseOver}
					{...filteredDataAttributes}
				/>
			);
		}

		return (
			<span
				ref={ref as React.Ref<HTMLSpanElement>}
				className={`${styles.letter} ${className}`}
				data-size={size}
				translate={autoTranslate}
				{...filteredDataAttributes}
			>
				{children}
			</span>
		);
	}
);

IconComponent.displayName = "IconComponent";

export default IconComponent;
